<template>
  <div class="subject-list-report">
    <!-- Control Bar -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Student-Subject Report</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ totalStudents }} Students
        </b-badge>
        <b-badge
          v-show="!loading && subjectGroups.length"
          variant="light-primary"
          class="ml-50"
        >
          {{ subjectGroups.length }} Subjects
        </b-badge>
      </div>
      
      <div class="action-buttons">
        <button class="btn-modern btn-print" type="button" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" type="button" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Loading student-subject report...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadReportData">
        Retry
      </b-button>
    </div>

    <!-- Report Content -->
    <div v-else class="report-page">
      <!-- Modern Header -->
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div v-if="schoolLogo" class="logo-wrapper mr-3">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">Student-Subject Report</span>
              <span class="date-range-text">
                <strong>Generated on {{ currentDateTime }}</strong>
              </span>
            </div>
            <p v-if="filterSummary" class="filter-line mb-0 mt-1">
              {{ filterSummary }}
            </p>
          </div>
        </div>
      </div>

      <div v-if="!subjectGroups.length" class="table-container">
        <div class="text-center py-5 no-data">
          <feather-icon icon="InboxIcon" size="32" class="text-muted mb-2" />
          <p>No students found for the selected criteria</p>
        </div>
      </div>

      <section
        v-for="(group, groupIndex) in subjectGroups"
        :key="'subject-' + (group.subjectID || groupIndex)"
        class="subject-block mb-4"
      >
        <div class="subject-heading">
          <span class="subject-heading__name">{{
            group.subjectName || "SUBJECT"
          }}</span>
          <span class="subject-heading__count">
            {{ (group.students || []).length }} students
          </span>
        </div>

        <div class="table-container">
          <table class="ledger-style-table">
            <thead>
              <tr>
                <th class="text-center" style="width: 8%">S.No</th>
                <th class="text-center" style="width: 14%">Gr.No</th>
                <th class="text-left" style="width: 28%">Student</th>
                <th class="text-left" style="width: 28%">Father Name</th>
                <th class="text-center" style="width: 22%">Class</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(student, index) in group.students || []"
                :key="'stu-' + groupIndex + '-' + index"
              >
                <td class="text-center font-weight-bold">
                  {{ student.sno || index + 1 }}
                </td>
                <td class="text-center gr-cell">
                  {{ student.grNo || "-" }}
                </td>
                <td class="text-left font-weight-bold">
                  {{ formatName(student.name) }}
                </td>
                <td class="text-left">
                  {{
                    formatName(student.fatherName || student.father_name)
                  }}
                </td>
                <td class="text-center">
                  {{ student.class || formatClass(student) }}
                </td>
              </tr>
              <tr v-if="!(group.students || []).length">
                <td colspan="5" class="text-center py-4 no-data">
                  No students
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "SubjectListReportPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      subjectGroups: [],
      totalStudents: 0,
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      db: "",
      cID: 0,
      status: "present",
      subjectIDs: "0",
      classIDs: "0",
      sectionIDs: "0",
      departmentID: 0,
      subjectLabel: "",
      classLabel: "",
      sectionLabel: "",
      statusLabel: "",
    };
  },
  computed: {
    currentDateTime() {
      return new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    filterSummary() {
      const parts = [
        this.statusLabel || this.statusDisplay(this.status),
        this.subjectLabel || "All Subjects",
        this.classLabel || "All Classes",
        this.sectionLabel || "All Sections",
      ];
      return parts.filter(Boolean).join(" · ");
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query || {};
      this.db = query.db || this.$store.state.userData.db;
      this.cID = Number(query.cID || this.$store.state.userData.cId || 0);
      this.status = query.status || "present";
      this.subjectIDs = this.normalizeIdsParam(
        query.subjectIDs ?? query.subjectID
      );
      this.classIDs = this.normalizeIdsParam(query.classIDs ?? query.classID);
      this.sectionIDs = this.normalizeIdsParam(
        query.sectionIDs ?? query.sectionID
      );
      this.departmentID = Number(query.departmentID || 0) || 0;
      this.subjectLabel = query.subjectLabel || "";
      this.classLabel = query.classLabel || "";
      this.sectionLabel = query.sectionLabel || "";
      this.statusLabel = query.statusLabel || this.statusDisplay(this.status);

      if (!this.db) {
        this.error =
          "Database identifier is missing. Please close and try again.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    normalizeIdsParam(value) {
      if (value == null || value === "" || value === "all") return "0";
      if (Array.isArray(value)) {
        const ids = value.map(Number).filter((id) => id > 0);
        return ids.length ? ids.join(",") : "0";
      }
      const raw = String(value).trim();
      if (!raw || raw === "0" || raw.toLowerCase() === "all") return "0";
      const ids = raw
        .split(",")
        .map((part) => Number(String(part).trim()))
        .filter((id) => id > 0);
      return ids.length ? ids.join(",") : "0";
    },
    statusDisplay(status) {
      const value = String(status || "present").toLowerCase();
      if (value === "all" || value === "") return "All Status";
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
    async loadCampusInfo() {
      try {
        const response = await axios.get(
          `${this.$store.state.domain}campuses`,
          {
            params: { db: this.db, cId: this.cID },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.campusName = match.name || match.organizationName || "MY SKOOL";
            this.campusBranch =
              this.cID == 0 ? "All Campuses" : match.branch || "";
            this.schoolLogo = match.logo || null;
            return;
          }
        }
      } catch (e) {
        console.warn("Campus info fetch failed, using fallback from store");
      }

      const currentBranch =
        (this.$store.state.branches || []).find((b) => b.id == this.cID) ||
        this.$store.state.currentBranch;
      if (currentBranch) {
        this.campusName =
          currentBranch.organizationName ||
          currentBranch.name ||
          currentBranch.branch ||
          "MY SKOOL";
        this.campusBranch = currentBranch.branch || "";
        this.schoolLogo = currentBranch.logo || null;
      }
    },
    buildApiParams() {
      const params = {
        db: this.db,
        cID: this.cID,
        classIDs: this.classIDs || "0",
        sectionIDs: this.sectionIDs || "0",
        subjectIDs: this.subjectIDs || "0",
      };

      const status = String(this.status || "").toLowerCase();
      if (status && status !== "all") {
        params.status = this.status;
      }

      if (this.departmentID > 0) {
        params.departmentID = this.departmentID;
      }

      return params;
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;

      try {
        const response = await axios.get(
          `${this.$store.state.domain}Students/SubjectListReport`,
          {
            params: this.buildApiParams(),
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );

        const payload = response.data || {};
        if (payload.status && payload.status !== "success") {
          this.error =
            payload.message || "Failed to load student-subject report.";
          this.subjectGroups = [];
          this.totalStudents = 0;
          return;
        }

        const groups = Array.isArray(payload.data) ? payload.data : [];
        this.subjectGroups = groups.map((group) => ({
          subjectID: group.subjectID,
          subjectName: group.subjectName || "SUBJECT",
          count: Number(group.count) || (group.students || []).length,
          students: Array.isArray(group.students) ? group.students : [],
        }));

        this.totalStudents =
          Number(payload.totalStudents) ||
          Number(payload.totalRows) ||
          this.subjectGroups.reduce(
            (sum, group) => sum + (group.students || []).length,
            0
          );
      } catch (err) {
        console.error("Subject list report error:", err);
        this.error =
          "Failed to retrieve report data. Please ensure the API endpoint is accessible.";
        this.subjectGroups = [];
        this.totalStudents = 0;
      } finally {
        this.loading = false;
      }
    },
    formatName(value) {
      const text = String(value || "").trim();
      return text ? text.toUpperCase() : "-";
    },
    formatClass(student) {
      const cls = String(
        student.className || student.class_name || student.cls || ""
      ).trim();
      const sec = String(
        student.sectionName || student.section_name || student.section || ""
      ).trim();
      if (cls && sec) return `${cls} - ${sec}`;
      return cls || sec || "-";
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.subject-list-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.report-page {
  background: #ffffff;
  padding: 3rem 2.5rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1200px;
  margin: 30px auto;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 20px;

  .school-logo-img {
    height: 130px;
    width: auto;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 2rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    letter-spacing: -0.5px;
  }

  .report-info-line {
    font-size: 1.1rem;
    color: #444;
    display: flex;
    align-items: center;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 15px;
    }

    .date-range-text {
      color: #555;
    }
  }

  .filter-line {
    font-size: 0.9rem;
    font-weight: 600;
    color: #636e72;
  }
}

.subject-heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  padding: 0.75rem 1rem;

  &__name {
    font-size: 1rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #2c3e50;
  }

  &__count {
    font-size: 0.85rem;
    font-weight: 700;
    color: #636e72;
  }
}

.ledger-style-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 0;
  border: 1px solid #dee2e6;
  background: white;

  th,
  td {
    border: 1px solid #dee2e6;
    padding: 12px 15px;
    box-sizing: border-box;
    font-size: 0.95rem;
  }

  thead {
    background: #2c3e50 !important;

    th {
      color: white !important;
      font-weight: 800;
      text-transform: uppercase;
      font-size: 0.9rem;
      letter-spacing: 0.5px;
    }
  }

  tbody tr:nth-child(even) {
    background-color: #f8f9fa;
  }

  tbody tr:hover {
    background: #f1f4ff;
  }

  .gr-cell {
    font-weight: 700;
    color: #6c5ce7;
  }
}

.no-data {
  color: #636e72;
  font-weight: 600;
}

@page {
  size: A4 portrait;
  margin: 0;
}

@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    color: #000 !important;
  }

  .subject-list-report {
    background: white !important;
    padding: 0 !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    padding: 1.2cm 0.8cm !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .subject-block {
    page-break-inside: auto;
    margin-bottom: 1rem !important;
  }

  .subject-heading {
    border: 1px solid #000 !important;
    background: #f1f2f6 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .ledger-style-table {
    font-size: 9pt !important;
    width: 100% !important;

    th,
    td {
      padding: 6px 10px !important;
      border: 1px solid #000 !important;
      color: #000 !important;
    }

    thead th {
      background: #f1f2f6 !important;
      color: #000 !important;
      font-weight: 800 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .gr-cell {
      color: #000 !important;
    }
  }

  .report-header-modern {
    margin-bottom: 25px !important;
    padding-bottom: 15px !important;
    border-bottom: 4px solid #000 !important;

    .school-logo-img {
      height: 140px !important;
      max-width: 350px !important;
      object-fit: contain !important;
    }

    .school-name-full {
      font-size: 1.8rem !important;
    }
  }
}
</style>
